import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { InstructorTable } from "../components/instructors/InstructorTable";
import { DeleteInstructorDialog } from "../components/instructors/DeleteInstructorDialog";
import { ExportInstructorsDialog } from "../components/instructors/ExportInstructorsDialog";
import { instructorService, Instructor } from "../services/instructorService";
import { useToast } from "../components/ui/ToastContainer";
import { PermissionDenied } from "../components/ui/PermissionDenied";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/Button";

export default function InstructorsPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = useAuth();

  if (!hasPermission('instructors:read')) {
    return <PermissionDenied requiredPermission="instructors:read" />;
  }
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Server-side State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialog States
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [selectedInstructor, setSelectedInstructor] =
    useState<Instructor | null>(null);

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchInstructors = useCallback(async () => {
    try {
      setIsLoading(true);

      // Map sort params to API format
      let apiSort = "newest";
      if (sortField === "createdAt") {
        apiSort = sortOrder === "asc" ? "oldest" : "newest";
      } else if (sortField === "students") {
        apiSort = "students";
      } else if (sortField === "courses") {
        apiSort = "courses";
      } else if (sortField === "experience") {
        apiSort = "experience";
      }

      const params: any = {
        page: currentPage,
        limit: pageSize,
        sort: apiSort,
        search: debouncedSearch,
      };

      if (statusFilter !== "all") {
        params.approvalStatus = statusFilter;
      }

      const response = await instructorService.getAllInstructors(params);

      const fetchedInstructors = response.instructors || [];
      // Handle response structure variations
      const total = (response.pagination as any)?.total || response.count || 0;

      setInstructors(fetchedInstructors);
      setTotalItems(total);
    } catch (error) {
      console.error("Failed to fetch instructors:", error);
      showError("Failed to load instructors");
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    debouncedSearch,
    statusFilter,
    showError,
  ]);

  useEffect(() => {
    fetchInstructors();
  }, [fetchInstructors]);

  const handleApproveInstructor = async (instructor: Instructor) => {
    try {
      await instructorService.updateApprovalStatus(instructor._id, "approved");
      showSuccess("Instructor approved successfully");
      fetchInstructors();
    } catch (error) {
      console.error("Failed to approve instructor:", error);
      showError("Failed to approve instructor");
    }
  };

  const handleRejectInstructor = async (instructor: Instructor) => {
    try {
      await instructorService.updateApprovalStatus(instructor._id, "rejected");
      showSuccess("Instructor rejected successfully");
      fetchInstructors();
    } catch (error) {
      console.error("Failed to reject instructor:", error);
      showError("Failed to reject instructor");
    }
  };

  const handleEditClick = (instructor: Instructor) => {
    navigate(`/instructors/edit/${instructor._id}`);
  };

  const handleDeleteClick = (instructor: Instructor) => {
    setSelectedInstructor(instructor);
    setIsDeleteModalOpen(true);
  };

  return (
    <DashboardLayout>
      <InstructorTable
        instructors={instructors}
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        sortField={sortField}
        sortOrder={sortOrder}
        onSearchChange={setSearchTerm}
        onStatusChange={(status) => {
          setStatusFilter(status);
          setCurrentPage(1);
        }}
        onSortChange={(field, order) => {
          setSortField(field);
          setSortOrder(order);
          setCurrentPage(1);
        }}
        onPageChange={setCurrentPage}
        onAddClick={() => navigate("/instructors/add")}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        onApproveClick={handleApproveInstructor}
        onRejectClick={handleRejectInstructor}
        extraHeaderActions={
          hasPermission("analytics:export") ? (
            <Button
              variant="outline"
              size="lg"
              className="whitespace-nowrap gap-2 w-full sm:w-auto h-[48px] sm:h-[44px] justify-center"
              onClick={() => setIsExportOpen(true)}
            >
              <Download size={16} />
              Export CSV
            </Button>
          ) : undefined
        }
      />

      <DeleteInstructorDialog
        isOpen={isDeleteModalOpen}
        instructor={selectedInstructor}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedInstructor(null);
        }}
        onSuccess={fetchInstructors}
      />

      <ExportInstructorsDialog
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        initialSearchTerm={searchTerm}
        initialStatus={
          statusFilter === "approved" ||
          statusFilter === "pending" ||
          statusFilter === "rejected"
            ? (statusFilter as "approved" | "pending" | "rejected")
            : "all"
        }
      />
    </DashboardLayout>
  );
}
